/**
 * Decisions for a phone that can hold more than one call at a time.
 *
 * Pure, and separated from the context because every rule here is one the vendored client does
 * not enforce and the UI must not guess at. Two of them are load-bearing:
 *
 *  - **Only one leg may be off hold.** Verto does not mix: two un-held calls means both far ends
 *    hear the rep, and the rep hears whichever stream the audio element happened to get last.
 *    Every transition between legs therefore holds the outgoing one *before* it touches the
 *    incoming one.
 *  - **Hold state is ours to keep.** The vendored client sends `verto.modify` and never updates
 *    its own `call.state`, so nothing downstream of it ever learns a call was held. The reply
 *    carries the new state, and `heldFromModifyResponse` is where we read it.
 *
 * Adapted from tap-ucaas `web/lib/softphone/transport.ts` (`toggleHold`, `holdAndAnswer`,
 * `endAndAnswer`, `switchToCall`) and its `secondaryRingingCall` selector.
 */

import type { RegistrationState } from "@/softphone/softphone-context";

/** The subset of a call these decisions need. Keeps them testable without the full `Call`. */
export interface CallLike {
  id: string;
  direction: "inbound" | "outbound";
  state: string;
}

/**
 * Whether a `verto.modify` reply says the leg is now held.
 *
 * The reply is `{ callID, action: "toggleHold", holdState: "held" | "active" }`. This is the only
 * announcement of a hold there is: the vendored client resolves the promise with it, updates
 * nothing, and emits no event. Read it wrong and the button toggles while the call carries on —
 * which is exactly what it did before this function existed.
 */
export function heldFromModifyResponse(res: unknown): boolean {
  if (!res || typeof res !== "object") return false;
  return (res as { holdState?: unknown }).holdState === "held";
}

/** A call the rep is actually on — the state that makes a second call a decision to make. */
export function isEngaged(state: string): boolean {
  return state === "active" || state === "held";
}

/** Still ours to hold, answer or hang up. */
export function isLive(state: string): boolean {
  return state !== "ended";
}

/**
 * The call ringing for attention while the rep is already on one, or null.
 *
 * The rep is not offered a choice for their *first* call — that is a plain answer/decline — nor
 * while their own outbound call is still ringing out, which is not yet a conversation to
 * protect.
 */
export function secondaryRinging<T extends CallLike>(calls: readonly T[], activeId: string | null): T | null {
  if (!activeId) return null;
  const active = calls.find((c) => c.id === activeId);
  if (!active || !isEngaged(active.state)) return null;
  return calls.find((c) => c.id !== activeId && c.direction === "inbound" && c.state === "ringing") ?? null;
}

/**
 * Whether it is safe to place a call alongside whatever is already up.
 *
 * The one thing that makes it unsafe is a leg *we* dialled that has not connected yet. Hold is
 * the only thing keeping two conversations apart, and `verto.modify` on a leg that is not up is
 * an error — so a second call placed next to a ringing outbound one would find nothing to park,
 * and the moment the first was answered both far ends would be hearing the rep.
 *
 * An inbound call ringing is not the same thing and does not block this: nothing connects it
 * until the rep answers, and answering parks the other leg first (`answerLeg`).
 */
export function canDialSecondLine(calls: readonly CallLike[]): boolean {
  return !calls.some(
    (c) => c.direction === "outbound" && (c.state === "trying" || c.state === "ringing"),
  );
}

/**
 * Whether this call can be handed over to the room the phone is already in.
 *
 * Two conditions, and both matter. The phone has to *be* in a conference — the switch command
 * moves the caller into `<extension>-room` and nothing creates that room but a leg joining it,
 * so offering this with no room open would hand a caller to an empty conference and hang up the
 * rep's only leg to them. And the call has to be one the rep is actually on: `uuid_transfer
 * -bleg` moves this leg's bridge partner, and a leg that is still dialling has no partner yet.
 *
 * The room leg itself is excluded because it is not bridged to anybody — it is *in* the
 * conference — so the transfer would find nothing to move and the button would lie.
 */
export function canMoveIntoRoom(
  call: CallLike,
  conference: { callId: string | null } | null,
): boolean {
  if (!conference) return false;
  if (conference.callId !== null && call.id === conference.callId) return false;
  return isEngaged(call.state);
}

/** Live calls other than `activeId` — what a swap can move to. */
export function swappableCalls<T extends CallLike>(calls: readonly T[], activeId: string | null): T[] {
  return calls.filter((c) => c.id !== activeId && isEngaged(c.state));
}

/**
 * Which leg should own the audio after the call list changed.
 *
 * Keeps the current one while it is live. When it ends, prefers a held call: that is the leg the
 * rep parked in order to take the one that just finished, and dropping them back to no call at
 * all — with a live customer still on hold — is the failure this exists to prevent.
 */
export function nextActiveCall<T extends CallLike>(
  calls: readonly T[],
  activeId: string | null,
  held: ReadonlySet<string>,
): string | null {
  if (activeId && calls.some((c) => c.id === activeId && isLive(c.state))) return activeId;
  const live = calls.filter((c) => isLive(c.state));
  const next = live.find((c) => held.has(c.id)) ?? live.find((c) => isEngaged(c.state)) ?? live[0];
  return next ? next.id : null;
}

/**
 * The hold/unhold moves needed to make `targetId` the un-held leg.
 *
 * Returned as a plan rather than performed, so the ordering rule — hold the outgoing leg before
 * un-holding the incoming one — is stated in one place and can be asserted in a test. Holding a
 * leg that is already held would un-hold it, which is why `held` is consulted rather than assumed.
 */
export function swapPlan(
  calls: readonly CallLike[],
  activeId: string | null,
  targetId: string,
  held: ReadonlySet<string>,
): { hold: string | null; unhold: string | null } {
  const outgoing =
    activeId && activeId !== targetId && calls.some((c) => c.id === activeId && isLive(c.state))
      ? activeId
      : null;
  return {
    hold: outgoing && !held.has(outgoing) ? outgoing : null,
    unhold: held.has(targetId) ? targetId : null,
  };
}

/**
 * The one call a warm transfer from `callId` could hand its party to, or null.
 *
 * A warm transfer bridges the two people the rep has on the line to each other and drops the
 * rep's own legs — `uuid_bridge` at the switch, `POST /phone/transfers` from here. So both ends
 * have to be a call the rep is actually *on*: a leg still dialling has no bridge partner to hand
 * over, and neither does the rep's own line into their conference room, which is a member of a
 * mixer rather than half of a bridge.
 *
 * **Exactly one candidate, or none at all.** With two parked calls this could only guess which
 * one the rep meant, and the wrong guess introduces the wrong two strangers and hangs up both
 * their reps — the same reason the swap button hides itself when there is more than one place to
 * swap to, and a much more expensive mistake. A rep with three calls swaps first, then transfers.
 */
export function warmTransferTarget<T extends CallLike>(
  calls: readonly T[],
  callId: string,
  conference: { callId: string | null } | null,
): T | null {
  const roomLeg = conference?.callId ?? null;
  const transferable = (c: CallLike) => isEngaged(c.state) && c.id !== roomLeg;
  const source = calls.find((c) => c.id === callId);
  if (!source || !transferable(source)) return null;
  const candidates = calls.filter((c) => c.id !== callId && transferable(c));
  return candidates.length === 1 ? (candidates[0] as T) : null;
}

/**
 * Whether this call can be blind transferred — sent to a destination the rep names, with no
 * introduction and no way back.
 *
 * The same shape as `canMoveIntoRoom`, and for the same reason: `uuid_transfer -bleg` moves this
 * leg's bridge PARTNER, so there has to be one. A call still dialling has no partner yet, and
 * the rep's own line into their room has none by nature — it is a conference member.
 *
 * Unlike a warm transfer this needs nothing else on the phone, which is the point of it: it is
 * what a rep does with the one call they have.
 */
export function canBlindTransfer(
  call: CallLike,
  conference: { callId: string | null } | null,
): boolean {
  if (conference?.callId != null && call.id === conference.callId) return false;
  return isEngaged(call.state);
}

/**
 * Every hand-over the phone can offer right now, collected in one place.
 *
 * The dock used to spread these across three controls on two kinds of row: a glyph for a blind
 * transfer on the live call, and "Move to my room" / "Connect to X" as buttons on whichever leg
 * happened to carry them. A rep thinks of all three as *transfer*, so the dock offers one
 * Transfer action, and this is what it lists — each caller who could go into the rep's room, the
 * one pair who could be introduced to each other, and each caller who could be sent to a number.
 *
 * Phone-wide rather than per call, because the leg the rep is *on* is often the wrong one to
 * ask: a rep sitting in their own room with a lead parked wants to move the lead, and the room
 * leg itself can go nowhere (`canMoveIntoRoom`). Each rule stays where it was; this only
 * collects their answers.
 *
 * The pair leads with the active call when it is half of one, so the wording ("Connect A with B")
 * starts with the person the rep is talking to. `warmTransferTarget` is symmetric, so either
 * order names the same bridge.
 *
 * A call that lives in a room the ACD made (ADR-029), named in `inCallRooms`, is neither moved
 * into the rep's room nor introduced to anybody: the rep's leg there is a member of that room
 * with no bridge partner for either to take. A number can still take it, by moving the caller's
 * own leg, which core-api does for such a call.
 */
export interface TransferOptions<T extends CallLike> {
  /** Callers who can be moved into the rep's conference room, one row each. */
  toRoom: T[];
  /** The one pair the rep could introduce to each other, or null. */
  pair: [T, T] | null;
  /** Callers who can be sent to a destination the rep types. */
  toNumber: T[];
}

export function transferOptions<T extends CallLike>(
  calls: readonly T[],
  activeId: string | null,
  conference: { callId: string | null } | null,
  inCallRooms: readonly string[] = [],
): TransferOptions<T> {
  const bridged = calls.filter((c) => !inCallRooms.includes(c.id));
  const toRoom = bridged.filter((c) => canMoveIntoRoom(c, conference));
  const toNumber = calls.filter((c) => canBlindTransfer(c, conference));
  const activeFirst = [...bridged].sort(
    (a, b) => Number(b.id === activeId) - Number(a.id === activeId),
  );
  let pair: [T, T] | null = null;
  for (const call of activeFirst) {
    const target = warmTransferTarget(bridged, call.id, conference);
    if (target) {
      pair = [call, target];
      break;
    }
  }
  return { toRoom, pair, toNumber };
}

export function hasTransferOptions(options: TransferOptions<CallLike>): boolean {
  return options.toRoom.length > 0 || options.pair !== null || options.toNumber.length > 0;
}

/**
 * Whether the phone OFFERS a destination the rep types out — "To a number or extension" on the
 * Transfer dialog, "Send the caller to a number" on a call in the rep's own number's room, and
 * "Transfer…" on a member of the rep's conference room.
 *
 * Off at Mustafa's ask (2026-09-18): hidden at the surface, not taken out. Everything under it is
 * untouched — `canBlindTransfer` above still answers, `phone.blindTransfer` still sends, and both
 * forms are still in the tree — so flipping this to true brings all three back at once. A surface
 * decision rather than a rule about the switch, which is why it does not live in
 * `canBlindTransfer` itself: the switch can still do this, we have stopped asking for it.
 */
export const SHOW_TRANSFER_TO_NUMBER: boolean = false;

/** `transferOptions` narrowed to what the phone is willing to offer. Every reader goes through
 *  this, so the Transfer tile and the Transfer dialog cannot disagree about whether there is
 *  anywhere to send a caller — a tile that opens an empty dialog is the failure here. */
export function offered<T extends CallLike>(options: TransferOptions<T>): TransferOptions<T> {
  return SHOW_TRANSFER_TO_NUMBER ? options : { ...options, toNumber: [] };
}

/**
 * Why the phone cannot place a call right now, in a sentence, or null when it can.
 *
 * The dock owns every remedy — connecting, taking the line over from another tab — so this says
 * what is wrong and points there rather than growing a second Connect button that would have to
 * be kept in step with the first.
 *
 * It lives here rather than on the phone page because dialling does not: the page's "Dial now"
 * card asks it, and so does every number a rep can click in their own call history. One sentence
 * per state in one place, so two surfaces cannot explain the same phone differently.
 *
 * `RegistrationState` is imported as a type only, which keeps this module free of React at
 * runtime — every other rule in it is testable without a switch and this one stays that way.
 */
export function dialBlockedReason(
  registration: RegistrationState,
  canDialSecond: boolean,
): string | null {
  switch (registration) {
    case "connecting":
      return "Your phone is still connecting to the switch.";
    case "standby":
      return "Your phone is registered in another browser tab. Take the line over from the phone panel to dial here.";
    case "failed":
      return "Your phone is not registered. Reconnect it from the phone panel.";
    case "idle":
      return "Your phone is not connected yet. Connect it from the phone panel.";
    case "registered":
      // A leg *we* dialled that has not connected yet is the one thing that makes a second call
      // unsafe — see `canDialSecondLine`. An inbound call ringing is not the same thing.
      return canDialSecond
        ? null
        : "A call you placed is still ringing. Wait for it to connect before dialling another.";
  }
}
